<template>
  <div class="success-container">
    <!-- Stage 1 to 5 -->
    <div
      v-if="stageData.stage >= 1 && stageData.stage <= 5"
      :class="['stage', `stage${stageData.stage}`]"
    >
      <div>
        <img
          :src="getImageForStage(stageData.stage)"
          :class="['successGifX', { 'reverse-anim': reverseAnimation }]"
        />
      </div>
      <div class="text-center successMSG">
        <h2 v-html="getMessageForStage(stageData.stage)"></h2>
      </div>
      <div class="btn-bottom text-center">
        <button @click="moveToNextStage" class="btn-next">
          Go Next Level&nbsp;&nbsp;<i class="fas fa-plane"></i>
        </button>
      </div>
    </div>

    <!-- Stage 6 (Final Stage) -->
    <div v-else-if="stageData.stage === 6">
      <div class="main-container">
        <div class="sub-container">
          <div class="sky">
            <div class="BGstars2"></div>
            <div class="BGstars3"></div>
            <div class="comet"></div>
          </div>
        </div>
      </div>
      <div>
        <div class="text-center successMSGCongrats">
          <h2>Congratulations!</h2>
          <h4>successfully</h4><br />
          <p>You completed the Survey</p>
          <div v-if="loading" class="spinner"></div>
        </div>
        <div class="btn-bottom text-center">
          <button @click="submitSurvey" class="btn-next" :disabled="loading">
            Submit&nbsp;<i class="fas fa-plane"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useRoute, useRouter } from "vue-router";
import { ref, computed, watch, onMounted } from "vue";
import surveyService from "@/services/surveyService";
import axiosInstance from "@/axiosInstance";

export default {
  setup() {
    const route = useRoute();
    const router = useRouter();
    const stageNumber = ref(parseInt(route.params.stageNumber) || 1);
    const stageData = ref(getStageData(stageNumber.value));
    const loading = ref(false);
    const reverseAnimation = ref(false);
    const totalStages = 5;

    onMounted(() => {
      if (surveyService.isSurveySubmitted().restrictSubmission) {
        router.push({ name: "SurveyEnd" });
      }
    });

    const hasNextStage = computed(() => stageNumber.value < totalStages);

    const moveToNextStage = () => {
      reverseAnimation.value = true;

      setTimeout(() => {
        const nextStageNumber = stageNumber.value + 1;
        router.push({ name: "SurveyStage", params: { stageNumber: nextStageNumber } });
      }, 700); // Duration should match reverse animation
    };

    watch(
      () => route.params.stageNumber,
      (newVal) => {
        stageNumber.value = parseInt(newVal) || 1;
        stageData.value = getStageData(stageNumber.value);
        reverseAnimation.value = false;
      }
    );

    function getStageData(stage) {
      return {
        stage: stage,
        name: `Stage ${stage} Name`,
        quote: `This is the quote for stage ${stage}.`,
      };
    }

    function getImageForStage(stage) {
      const images = {
        1: require("@/assets/img/successImg/1.gif"),
        2: require("@/assets/img/successImg/2.gif"),
        3: require("@/assets/img/successImg/3.gif"),
        4: require("@/assets/img/successImg/4.gif"),
        5: require("@/assets/img/successImg/5.gif"),
      };
      return images[stage];
    }

    function getMessageForStage(stage) {
      const messages = {
        1: "Awesome job!<br>Your input is very helpful",
        2: "Great job!<br>Your opinion truly matters.",
        3: "You're making a difference,<br>one answer at a time.",
        4: "Awesome!<br>Your response has been recorded.",
        5: "We appreciate your input—<br>you're helping shape the future.",
      };
      return messages[stage];
    } 

    const submitSurvey = async () => {
      loading.value = true;
      const qrcode = localStorage.getItem("qrcode");

      try {
        await axiosInstance.post("public/answers", {
          qr_id: qrcode,
          question_id: 1,
          answer: surveyService.getAnswers(),
          unique_id: surveyService.getUniqueId(), // ✅ Added unique_id
        });

        surveyService.setSurveyCompleted();
      } catch (error) {
        console.error("Error submitting survey:", error);
        alert("Failed to submit survey. Please try again.");
      } finally {
        loading.value = false;
        router.push({ name: "SurveyEnd" });
      }
    };

    return {
      stageData,
      hasNextStage,
      moveToNextStage,
      submitSurvey,
      loading,
      reverseAnimation,
      getImageForStage,
      getMessageForStage,
    };
  },
};
</script>

<style scoped>
.successGifX {
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translate(-50%, -100%);
  height: 260px;
  animation: entryAnim 1.2s forwards;
}

.reverse-anim {
  animation: exitAnim 0.7s forwards !important;
}

@keyframes entryAnim {
  from {
    top: 0px;
    opacity: 0;
  }
  to {
    top: 64%;
    opacity: 1;
  }
}

@keyframes exitAnim {
  from {
    top: 64%;
    opacity: 1;
  }
  to {
    top: 0px;
    opacity: 0;
  }
}

@media only screen and (max-width: 768px) {
  .successGifX {
    transform: translate(-50%, -100%);
  }
}
</style>
